import { MutableRefObject, useEffect } from "react";
import { StaticLineageDetails } from "../types";
import { ColumnNode, SeeMoreNode, SelfConnectingEdge, SqlTableNode, OpNode } from "./CustomNodes";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  Node,
  ReactFlowInstance,
  ReactFlowProvider,
} from "reactflow";
import { createNewNodesEdges, layoutElementsOnCanvas } from "../graph";
import { SidebarModal } from "./sidebarModal/SidebarModal";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { StaticTableDetails } from "./TableDetails";
import {
  setHighlightedNodes,
  setNodesCost,
  setNodesSavingsPerformance,
  setSelectedNode,
  setSelectedTable,
  setSqlLineageDetails,
} from "../context/LineageSlice";
import {
  createOpNode,
  createTableEdge,
  createTableNode,
  findSourcesSinks,
  getCurrentTableId,
  handleTableNavigation,
} from "../utils";
import { LineageModal } from "./LineageModals";
import { NODE_TYPE_SHORTHAND } from "./Column";
import { LineageState } from "../context/types";
import { FitViewOnNodesInitialized } from "./FitViewOnNodesInitialized";

const nodeTypes = {
  table: SqlTableNode,
  seeMore: SeeMoreNode,
  column: ColumnNode,
  operator: OpNode,
};
const edgeTypes = {
  selfConnecting: SelfConnectingEdge,
};

type SqlLineageProps = {
  tableEdges: [string, string][];
  details: StaticLineageDetails;
  flow: MutableRefObject<ReactFlowInstance<unknown, unknown> | undefined>;
  nodePositions?: Record<string, [number, number]>;
};

const SqlLineage = ({ flow, tableEdges, details, nodePositions }: SqlLineageProps) => {
  const {
    state: { selectedTable },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();

  useEffect(() => {
    lineageDispatch(setSqlLineageDetails({ details, tableEdges }));
  }, [details, lineageDispatch, tableEdges]);

  useEffect(() => {
    setTimeout(async () => {
      const _createTable = (table: string) => ({
        table,
        label: details[table].name || table,
        upstreamCount: 0,
        downstreamCount: 0,
        nodeType: details[table].type || "cte",
        isExternalProject: false,
        tests: [],
      });
      if (nodePositions) {
        const nodes: Node[] = Object.keys(nodePositions).map(n => {
          const opType = details[n].type;
          if (opType in NODE_TYPE_SHORTHAND) {
            return createTableNode(_createTable(n), 0, "");
          }
          return createOpNode(n, 0, "", details[n]);
        });
        const edges = tableEdges.map(([src, dst]) =>
          createTableEdge(nodePositions[src][1], nodePositions[dst][1], src, dst, true, true)
        );

        for (const n of nodes) {
          if (!nodePositions[n.id]) continue;
          const [x, y] = nodePositions[n.id];
          n.position = { x, y };
        }
        flow.current?.setNodes(nodes);
        flow.current?.setEdges(edges);
        return;
      }
      const { sinks } = findSourcesSinks(tableEdges);
      let nodes: Node[] = sinks.map(n => createTableNode(_createTable(n), 0, ""));
      let edges: Edge[] = [];
      const queue = [...sinks];
      const visited: Record<string, boolean> = {};
      const getConnectedTables = (right: boolean, curr: string) => {
        const connectedTables = right
          ? tableEdges.filter(([src]) => src === curr).map(([, dst]) => dst)
          : tableEdges.filter(([, dst]) => dst === curr).map(([src]) => src);
        const currLevel = nodes.find(n => n.id === curr)?.data?.level || 0;
        createNewNodesEdges(
          nodes,
          edges,
          connectedTables.map(_createTable),
          curr,
          right,
          currLevel,
          10000,
          true,
          details
        );
        return connectedTables;
      };
      while (queue.length > 0) {
        const curr = queue.pop()!;
        if (visited[curr]) continue;
        visited[curr] = true;
        queue.push(...getConnectedTables(false, curr));
      }

      layoutElementsOnCanvas(nodes, edges, true);
      flow.current?.setNodes(nodes);
      flow.current?.setEdges(edges);
    }, 500);
  }, [details, flow, tableEdges, nodePositions]);

  useEffect(() => {
    const handleHighlightedNodes = (e: CustomEvent<string[]>) => {
      lineageDispatch(setHighlightedNodes(e.detail));
    };

    const handleSelectedNodes = (e: CustomEvent<string>) => {
      lineageDispatch(setSelectedNode(e.detail));
    };

    const handleFitView = () => {
      setTimeout(() => {
        flow.current?.fitView({ duration: 500 });
      }, 500);
    };

    const handleNodesSavingsPerformance = (
      e: CustomEvent<LineageState["nodeSavingsPerformance"]>
    ) => {
      lineageDispatch(setNodesSavingsPerformance(e.detail));
    };

    const handleNodesCost = (e: CustomEvent<LineageState["nodesCost"]>) => {
      lineageDispatch(setNodesCost(e.detail));
    };
    // @ts-ignore
    document.addEventListener("onHighlightedNodes", handleHighlightedNodes);
    // @ts-ignore
    document.addEventListener("onSelectedNodes", handleSelectedNodes);
    // @ts-ignore
    document.addEventListener("onNodesSavingsPerformance", handleNodesSavingsPerformance);
    // @ts-ignore
    document.addEventListener("onNodesCost", handleNodesCost);
    document.addEventListener("fitView", handleFitView);

    return () => {
      // @ts-ignore
      document.removeEventListener("highlightedNodes", handleHighlightedNodes);
      // @ts-ignore
      document.removeEventListener("onSelectedNodes", handleSelectedNodes);
      // @ts-ignore
      document.removeEventListener("onNodesSavingsPerformance", handleNodesSavingsPerformance);
      // @ts-ignore
      document.removeEventListener("onNodesCost", handleNodesCost);
      document.removeEventListener("fitView", handleFitView);
    };
  }, [flow, lineageDispatch]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div style={{ width: "100%", height: "100%" }}>
        <ReactFlowProvider>
          <ReactFlow
            defaultNodes={[]}
            defaultEdges={[]}
            onInit={_flow => (flow.current = _flow)}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            style={{ background: "var(--bg-color)" }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.05}
            preventScrolling={false}
            zoomOnScroll={false}
            panOnScroll={false}
            zoomOnPinch
          >
            <FitViewOnNodesInitialized />
            <Background />
            <Controls />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
      <LineageModal />
      <SidebarModal
        isOpen={Boolean(selectedTable)}
        closeModal={() => lineageDispatch(setSelectedTable(""))}
        width="30vw"
      >
        {Boolean(selectedTable) && (
          <StaticTableDetails
            currentTableId={getCurrentTableId()}
            onNavigate={handleTableNavigation}
          />
        )}
      </SidebarModal>
    </div>
  );
};

export default SqlLineage;
